import { Modal, Tabs, notification } from "antd";
import { cloneDeep } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import UploadForm from "../../Common/Components/UploadForm";
import { useCreatePerson } from "../../entities/api";
import { useHttp } from "../../hooks/useHttp";
import AddPersonForm from "./AddPersonForm";
import { useNotification } from "~hooks/useNotification";
import {PermissionNames} from "~enums/permissions.ts";

const getItems = (data, images, dropCallback, disabled) => {
  return [
    {
      key: "head",
      label: `Head Shot Photo`,
      children: (
        <UploadForm
          imageType="PERSON_HEAD"
          dataId={data.personId}
          image={images.head}
          imageFor="PERSON"
          addModalMode
          handleChangeFile={dropCallback}
          modalLoading={disabled}
          permissionKey={PermissionNames.PERSON}
        />
      ),
    },
    {
      key: "waist",
      label: `Waist Shot Photo`,
      children: (
        <UploadForm
          imageType="PERSON_WAIST"
          dataId={data.personId}
          image={images.waist}
          imageFor="PERSON"
          addModalMode
          handleChangeFile={dropCallback}
          modalLoading={disabled}
          permissionKey={PermissionNames.PERSON}
        />
      ),
    },
    {
      key: "posed",
      label: `Posed / Action Shot Photo`,
      children: (
        <UploadForm
          imageType="PERSON_POSE"
          dataId={data.personId}
          image={images.pose}
          imageFor="PERSON"
          addModalMode
          handleChangeFile={dropCallback}
          modalLoading={disabled}
          permissionKey={PermissionNames.PERSON}
        />
      ),
    },
  ];
};

const ManagePersonPhoto = ({ dropCallback, images, formDisabled }) => {
  return (
    <div className="card-container tabs-wrap">
      <Tabs
        destroyInactiveTabPane={true}
        tabBarGutter={0}
        items={getItems(
          {},
          {
            head: images.PERSON_HEAD,
            waist: images.PERSON_WAIST,
            pose: images.PERSON_POSE,
          },
          dropCallback,
          formDisabled,
        )}
      />
    </div>
  );
};

const formatData = (values) => {
  const copy = cloneDeep(values);

  if (copy?.dob) {
    copy.dob = copy.dob?.format("YYYY-MM-DD");
  }

  return copy;
};

const AddPersonModal = (props) => {
  const { loading, error, request, cleanError } = useHttp();
  const { isModalOpen, handleClose, finishCb, closeAfterAdd, quickLink } = props;
  const [formRef, setFormRef] = useState(null);
  const { sport, organizationId } = useParams();
  const [images, setImages] = useState({
    PERSON_HEAD: { file: null, url: null },
    PERSON_WAIST: { file: null, url: null },
    PERSON_POSE: { file: null, url: null },
  });
  const { mutateAsync: createFn, isSuccess: isCreateSuccess, isPending: isCreatePending } = useCreatePerson();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const notification = useNotification();

  const handleSetFormRef = useCallback((form) => {
    setFormRef(form);
  }, []);

  const addPersonRequest = async (data) => {
    try {
      const res = await createFn(data);
      if (res?.data?.length) {
        return res.data[0];
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleLoadImage = useCallback(
    async (file, personId, imageType) => {
      if (file) {
        const endpoint = `/v1/${sport}/o/${organizationId}/images/for/PERSON/${personId}/${imageType}`;
        try {
          await request({
            url: endpoint,
            method: "POST",
            fileBody: file,
          });
        } catch (e) {
          console.error(e);
        }
      }
    },
    [organizationId, request, sport],
  );

  useEffect(() => {
    if (error) {
      notification.error({
        message: "Can't add person",
        description: error,
        placement: "bottomRight",
      });
      cleanError();
    }
  }, [cleanError, error]);

  const clearForm = useCallback(() => {
    setImages({
      PERSON_HEAD: { file: null, url: null },
      PERSON_WAIST: { file: null, url: null },
      PERSON_POSE: { file: null, url: null },
    });
    formRef.resetFields([
      "nameFullLocal",
      "nameFullLatin",
      "dob",
      "nationality",
      "gender",
      "status",
      ["additionalDetails", "weight"],
      ["additionalDetails", "height"],
      ["social", "website"],
      ["social", "facebook"],
      ["social", "twitter"],
      ["social", "wikipedia"],
      ["social", "instagram"],
    ]);
  }, [formRef]);

  useEffect(() => {
    if (isCreateSuccess) {
      notification.success({
        message: "Person added successfully",
        placement: "bottomRight",
      });
    }
  }, [isCreateSuccess]);

  const handleSendForm = async () => {
    try {
      const values = await formRef.validateFields();
      const formatted = formatData(values);

      setFormSubmitting(true);
      const data = await addPersonRequest(formatted);
      //TODO rewrite to react query
      await handleLoadImage(images.PERSON_HEAD.file, data.personId, "PERSON_HEAD");
      await handleLoadImage(images.PERSON_WAIST.file, data.personId, "PERSON_WAIST");
      await handleLoadImage(images.PERSON_POSE.file, data.personId, "PERSON_POSE");
      setFormSubmitting(false);
      clearForm();
      if (closeAfterAdd && finishCb) {
        finishCb(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseModal = useCallback(() => {
    handleClose();
    if (finishCb) {
      finishCb();
    }
  }, [handleClose, finishCb]);

  const dropCallback = useCallback(
    (type, file) => {
      setImages({ ...images, [type]: file });
    },
    [images, setImages],
  );
  return (
    <Modal
      destroyOnClose={true}
      width={800}
      title="Add Person"
      open={isModalOpen}
      onCancel={handleCloseModal}
      onOk={handleSendForm}
      okText={"Add"}
      okButtonProps={{ loading: loading || isCreatePending }}
    >
      <AddPersonForm
        quickLink={quickLink}
        formDisabled={formSubmitting}
        handleSetFormRef={handleSetFormRef}
        {...props}
      />
      <ManagePersonPhoto dropCallback={dropCallback} images={images} formDisabled={formSubmitting}/>
    </Modal>
  );
};

export default AddPersonModal;
